import { Copy, Eye, Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import { formatDateTimeBs } from "@/lib/format";
import { AssignmentStatusBadge } from "./AssignmentStatusBadge";
import type { TestAssignment } from "@/types/test";

interface Props {
  assignment: TestAssignment;
  onView: (id: string) => void;
  onDelete: (id: string) => void;
}

export function AssignmentCard({ assignment, onView, onDelete }: Props) {
  const { t } = useTranslation();
  const url = `${window.location.origin}/test/${assignment.token}`;
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success(t("tests.therapist.copied"));
  };

  return (
    <div className="flex items-center gap-4 border-b border-border/60 py-3.5 first:pt-0.5 last:border-b-0 last:pb-0.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate text-sm font-medium text-foreground">
            {assignment.clientFirstName} {assignment.clientLastName}
          </h3>
          <AssignmentStatusBadge status={assignment.status} />
        </div>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <span className="truncate">{assignment.snapshotName}</span>
          <span aria-hidden className="text-muted-foreground/50">·</span>
          <span className="tabular-nums">
            {assignment.status === "submitted"
              ? t("tests.therapist.submittedAt", {
                  time: formatDateTimeBs(assignment.submittedAt!),
                })
              : t("tests.therapist.expiresAt", {
                  time: formatDateTimeBs(assignment.expiresAt),
                })}
          </span>
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {assignment.status === "active" && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={copy}
            title={t("tests.therapist.copyLink")}
            aria-label={t("tests.therapist.copyLink")}
          >
            <Copy />
          </Button>
        )}
        {assignment.status === "submitted" && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onView(assignment.id)}
            title={t("tests.therapist.viewResult")}
            aria-label={t("tests.therapist.viewResult")}
          >
            <Eye />
          </Button>
        )}
        {assignment.status !== "submitted" && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onDelete(assignment.id)}
            aria-label={t("common.delete")}
            className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash />
          </Button>
        )}
      </div>
    </div>
  );
}
